import { useState } from "react";
import { format, parseISO } from "date-fns";
import { bg } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { addDoc, Timestamp, collection } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { db } from "../firebase/config";
import { LEAD_TYPES, submitLeadEmail } from "../services/leadEmail";
import { Button } from "./ui/button";
import { Calendar } from "./ui/calendar";
import { Field, FieldError, FieldGroup, FieldLabel } from "./ui/field";
import { Input } from "./ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Textarea } from "./ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "./ui/toggle-group";

const DEALERSHIP_EMAIL = "pleven@motopfohe.bg";
const COLLECTION_NAME = "pleven-open-days-2026";

const MODEL_OPTIONS = [
  "Puma Gen-E",
  "Puma Hybrid",
  "Kuga Full Hybrid",
  "Tourneo Courier",
  "Tourneo Connect",
  "Explorer",
  "Capri",
  "Mustang Mach-E",
  "Все още не знам",
];

const REQUEST_OPTIONS = [
  {
    value: "testdrive",
    title: "Искам тест драйв",
  },
  {
    value: "offer",
    title: "Искам оферта",
  },
];

const TIME_OPTIONS = [
  "09:00",
  "10:00",
  "11:00",
  "12:00",
  "13:00",
  "14:00",
  "15:00",
  "16:00",
  "17:00",
];

const formSchema = z.object({
  requestType: z.enum(["testdrive", "offer"], {
    error: "Моля, избери тип запитване",
  }),
  name: z.string().trim().min(2, "Името трябва да е поне 2 символа"),
  phone: z
    .string()
    .trim()
    .regex(
      /^(?:\+359|0)\s?\d{1,2}\s?\d{3}\s?\d{3,4}$/,
      "Моля, въведи валиден телефонен номер",
    ),
  email: z.string().trim().email("Моля, въведи валиден имейл адрес"),
  model: z.string().trim().min(1, "Моля, избери модел"),
  preferredDate: z.string().optional(),
  preferredTime: z.string().optional(),
  message: z.string().optional(),
  website: z.string().optional(),
  dealership: z.string().optional(),
  dealershipLabel: z.string().optional(),
  campaignName: z.string().optional(),
  title: z.string().optional(),
});

function PlevenOpenContactForm() {
  const [submitting, setSubmitting] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      requestType: "testdrive",
      model: "",
      dealership: DEALERSHIP_EMAIL,
      dealershipLabel: "Ford Плевен",
      preferredDate: "",
      preferredTime: "",
      message: "",
      website: "",
    },
  });

  const requestType = watch("requestType");
  const model = watch("model");
  const preferredDate = watch("preferredDate");

  const onSubmit = async (data) => {
    if (data.website) {
      return;
    }

    setSubmitting(true);

    try {
      await addDoc(collection(db, COLLECTION_NAME), {
        name: data.name ?? "",
        email: data.email ?? "",
        phone: data.phone ?? "",
        model: data.model ?? "",
        requestType: data.requestType ?? "testdrive",
        preferredDate: data.preferredDate ?? "",
        preferredTime: data.preferredTime ?? "",
        message: data.message ?? "",
        dealership: DEALERSHIP_EMAIL,
        dealershipLabel: "Ford Плевен",
        campaign: "PlevenOpenDaysJune2026",
        date: Timestamp.fromDate(new Date()),
      });

      await submitLeadEmail({
        type:
          data.requestType === "testdrive"
            ? LEAD_TYPES.CAMPAIGN_TESTDRIVE
            : LEAD_TYPES.CAMPAIGN_OFFER,
        customer: {
          name: data.name,
          email: data.email,
          phone: data.phone,
        },
        dealership: {
          email: DEALERSHIP_EMAIL,
          label: "Ford Плевен",
        },
        vehicle: {
          model: data.model,
        },
        campaign: {
          slug: "pleven-open-days-2026",
        },
        source: {
          pageUrl: window.location.href,
        },
        message: data.message,
        title:
          data.requestType === "testdrive"
            ? "Заявка за тест драйв - Дни на отворени врати Ford Плевен"
            : "Запитване за оферта - Дни на отворени врати Ford Плевен",
      });

      navigate(
        data.requestType === "testdrive"
          ? "/thank-you-testdrive"
          : "/thank-you-offer",
      );
    } catch (error) {
      console.error("Грешка при изпращане на формата:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      id="plevenOpenCampaignForm"
      onSubmit={handleSubmit(onSubmit)}
      className="grid w-full gap-8"
      noValidate
    >
      <input
        type="text"
        tabIndex="-1"
        autoComplete="off"
        className="hidden"
        {...register("website")}
      />

      <input
        type="hidden"
        value={DEALERSHIP_EMAIL}
        {...register("dealership")}
      />
      <input
        type="hidden"
        value="Ford Плевен"
        {...register("dealershipLabel")}
      />
      <input
        type="hidden"
        value="Дни на отворени врати във Ford Плевен"
        {...register("campaignName")}
      />
      <input type="hidden" value={requestType} {...register("requestType")} />
      <input type="hidden" value={model} {...register("model")} />
      <input
        type="hidden"
        value={preferredDate}
        {...register("preferredDate")}
      />
      <input
        type="hidden"
        value={watch("preferredTime")}
        {...register("preferredTime")}
      />

      <FieldGroup className="gap-8">
        <Field data-invalid={!!errors.requestType}>
          <FieldLabel>
            Тип запитване <span className="text-red-600">*</span>
          </FieldLabel>
          <ToggleGroup
            type="single"
            value={requestType}
            onValueChange={(value) => {
              if (!value) return;
              setValue("requestType", value, { shouldValidate: true });
            }}
            variant="outline"
            spacing={8}
            className="flex flex-wrap w-full gap-3"
          >
            {REQUEST_OPTIONS.map((option) => (
              <ToggleGroupItem
                key={option.value}
                value={option.value}
                className="h-11 min-w-0 flex-1 rounded-xl border border-slate-200 px-4 text-sm font-normal text-slate-600 data-[state=on]:border-fordBlue data-[state=on]:bg-[rgba(16,43,78,0.06)] data-[state=on]:text-fordBlue"
              >
                {option.title}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <FieldError>{errors.requestType?.message}</FieldError>
        </Field>

        <Field data-invalid={!!errors.name}>
          <FieldLabel htmlFor="name">
            Име и фамилия <span className="text-red-600">*</span>
          </FieldLabel>
          <Input
            id="name"
            aria-invalid={!!errors.name}
            placeholder="Напр. Иван Петров"
            className="h-11 rounded-xl border-slate-200 bg-[rgb(248,250,252)] px-4 text-base shadow-none focus-visible:border-fordBlue focus-visible:ring-0"
            {...register("name")}
          />
          <FieldError>{errors.name?.message}</FieldError>
        </Field>

        <Field data-invalid={!!errors.phone}>
          <FieldLabel htmlFor="phone">
            Телефон <span className="text-red-600">*</span>
          </FieldLabel>
          <Input
            id="phone"
            type="tel"
            aria-invalid={!!errors.phone}
            placeholder="Напр. 0888 123 456"
            className="h-11 rounded-xl border-slate-200 bg-[rgb(248,250,252)] px-4 text-base shadow-none focus-visible:border-fordBlue focus-visible:ring-0"
            {...register("phone")}
          />
          <FieldError>{errors.phone?.message}</FieldError>
        </Field>

        <Field data-invalid={!!errors.email}>
          <FieldLabel htmlFor="email">
            Имейл <span className="text-red-600">*</span>
          </FieldLabel>
          <Input
            id="email"
            type="email"
            aria-invalid={!!errors.email}
            placeholder="Напр. ivan@example.com"
            className="h-11 rounded-xl border-slate-200 bg-[rgb(248,250,252)] px-4 text-base shadow-none focus-visible:border-fordBlue focus-visible:ring-0"
            {...register("email")}
          />
          <FieldError>{errors.email?.message}</FieldError>
        </Field>

        <Field data-invalid={!!errors.model}>
          <FieldLabel htmlFor="model-trigger">
            Избери модел: <span className="text-red-600">*</span>
          </FieldLabel>
          <Select
            value={model}
            onValueChange={(value) =>
              setValue("model", value, { shouldValidate: true })
            }
          >
            <SelectTrigger
              id="model-trigger"
              aria-invalid={!!errors.model}
              className="h-12 w-full rounded-xl border-slate-200 bg-[rgb(248,250,252)] px-4 text-base font-fordAntennaRegular shadow-none focus-visible:border-fordBlue focus-visible:ring-0"
            >
              <SelectValue placeholder="Моля, избери" />
            </SelectTrigger>
            <SelectContent className="bg-white opacity-100 font-fordAntennaRegular">
              <SelectGroup>
                {MODEL_OPTIONS.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          <FieldError>{errors.model?.message}</FieldError>
        </Field>

        {requestType === "testdrive" ? (
          <>
            <Field>
              <FieldLabel htmlFor="preferredDateButton">
                Предпочитана дата
              </FieldLabel>
              <Popover
                open={isDatePickerOpen}
                onOpenChange={setIsDatePickerOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    id="preferredDateButton"
                    variant="outline"
                    type="button"
                    className="h-12 w-full justify-between rounded-xl border-slate-200 bg-[rgb(248,250,252)] px-4 text-base font-fordAntennaRegular font-normal text-slate-700 shadow-none hover:bg-white"
                  >
                    {preferredDate
                      ? format(parseISO(preferredDate), "dd.MM.yyyy", {
                          locale: bg,
                        })
                      : "Избери дата"}
                    <CalendarIcon data-icon="inline-end" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[25rem] border border-slate-200 bg-white p-0 font-fordAntennaRegular shadow-xl opacity-100"
                  align="start"
                >
                  <Calendar
                    mode="single"
                    locale={bg}
                    className="w-full p-4 bg-white font-fordAntennaRegular"
                    defaultMonth={new Date(2026, 5, 1)}
                    startMonth={new Date(2026, 5, 1)}
                    endMonth={new Date(2026, 5, 30)}
                    hideNavigation
                    selected={
                      preferredDate ? parseISO(preferredDate) : undefined
                    }
                    onSelect={(date) => {
                      setValue(
                        "preferredDate",
                        date ? format(date, "yyyy-MM-dd") : "",
                        { shouldValidate: true },
                      );
                      if (date) {
                        setIsDatePickerOpen(false);
                      }
                    }}
                    disabled={(date) =>
                      date < new Date(2026, 5, 1) ||
                      date > new Date(2026, 5, 30)
                    }
                  />
                </PopoverContent>
              </Popover>
            </Field>

            <Field>
              <FieldLabel htmlFor="preferredTime">Предпочитан час</FieldLabel>
              <Select
                value={watch("preferredTime")}
                onValueChange={(value) =>
                  setValue("preferredTime", value, { shouldValidate: true })
                }
              >
                <SelectTrigger
                  id="preferredTime"
                  className="h-12 w-full rounded-xl border-slate-200 bg-[rgb(248,250,252)] px-4 text-base font-fordAntennaRegular shadow-none focus-visible:border-fordBlue focus-visible:ring-0"
                >
                  <SelectValue placeholder="Избери час" />
                </SelectTrigger>
                <SelectContent className="bg-white opacity-100 font-fordAntennaRegular">
                  <SelectGroup>
                    {TIME_OPTIONS.map((time) => (
                      <SelectItem key={time} value={time}>
                        {time}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </Field>
          </>
        ) : null}

        <Field>
          <FieldLabel htmlFor="message">Допълнителни детайли</FieldLabel>
          <Textarea
            id="message"
            rows={5}
            placeholder="Ако искаш, добави предпочитания или въпрос"
            className="min-h-[140px] rounded-xl border-slate-200 bg-[rgb(248,250,252)] px-4 py-3 text-base shadow-none focus-visible:border-fordBlue focus-visible:ring-0"
            {...register("message")}
          />
        </Field>
      </FieldGroup>

      <input
        type="hidden"
        value={
          requestType === "testdrive"
            ? "Заявка за тест драйв - Дни на отворени врати Ford Плевен"
            : "Запитване за оферта - Дни на отворени врати Ford Плевен"
        }
        {...register("title")}
      />

      <div className="flex flex-col gap-4 pt-2 sm:flex-row sm:items-center sm:justify-end">
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center justify-center px-8 py-3 text-base text-white transition rounded-full bg-fordBlue hover:bg-fordLight disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting
            ? "Изпращане..."
            : requestType === "testdrive"
              ? "Запиши ме за тест драйв"
              : "Изпрати запитване"}
        </button>
      </div>
    </form>
  );
}

export default PlevenOpenContactForm;
